import { useId, useLayoutEffect, useRef } from 'react'
import { useIntl } from 'react-intl'

import { MessagesSquareIcon } from 'lucide-react'

import { Button } from '@/components/atoms/Button/Button.tsx'
import { EmptyState } from '@/components/atoms/EmptyState'
import { NotificationSound } from '@/components/molecules/NotificationSound/NotificationSound.tsx'
import { SupportAgentTitle, type SupportAgentTitleProps } from '@/components/molecules/SupportAgentTitle'
import { SupportSummary, type SupportSummaryProps } from '@/components/molecules/SupportSummary'
import { SupportThreadItem } from '@/components/molecules/SupportThreadItem'
import { SupportAgentComposer, type SupportAgentComposerProps } from '@/components/organisms/SupportAgentComposer'
import { SupportAgentDetails, type SupportAgentDetailsProps } from '@/components/organisms/SupportAgentDetails'
import { SupportAgentQueue, type SupportAgentQueueProps } from '@/components/organisms/SupportAgentQueue'
import { type SupportConversationItem } from '@/components/organisms/SupportConversation'

export interface SupportAgentConversation {
  readonly number: number
  readonly meta: string
  readonly title: string
  readonly rename?: Pick<SupportAgentTitleProps, 'saving' | 'error' | 'onRename'>
  readonly items: ReadonlyArray<SupportConversationItem>
  readonly summary?: SupportSummaryProps
  readonly composer: SupportAgentComposerProps
}

export interface SupportAgentViewProps {
  readonly agentLabel: string
  readonly soundEnabled: boolean
  readonly onSoundToggled: () => void
  readonly onBackToPilote: () => void
  readonly queue: SupportAgentQueueProps
  readonly conversation: SupportAgentConversation | null
  readonly details: SupportAgentDetailsProps | null
}

const PINNED_THRESHOLD_PX = 32

const SupportAgentThread = ({ number, meta, title, rename, items, summary, composer }: SupportAgentConversation) => {
  const titleId = useId()
  const threadRef = useRef<HTMLDivElement>(null)
  const pinnedToBottom = useRef(true)
  const summaryLayout = JSON.stringify(summary ?? null)

  useLayoutEffect(() => {
    const thread = threadRef.current
    if (thread) {
      thread.scrollTop = thread.scrollHeight
      pinnedToBottom.current = true
    }
  }, [number, items.length])

  useLayoutEffect(() => {
    const thread = threadRef.current
    if (thread && pinnedToBottom.current) {
      thread.scrollTop = thread.scrollHeight
    }
  }, [summaryLayout])

  return (
    <section
      aria-labelledby={titleId}
      className="flex h-full min-w-0 flex-[999_1_480px] flex-col overflow-hidden rounded-[20px] bg-white">
      <header className="flex flex-col gap-2 border-b-[12px] border-gray-400 px-6 pt-4 pb-3.5">
        <span className="text-sm text-gray-600">{meta}</span>
        <SupportAgentTitle key={number} id={titleId} title={title} {...rename} />
      </header>
      <div
        ref={threadRef}
        data-testid="support-thread"
        className="relative flex flex-1 flex-col overflow-y-auto"
        onScroll={({ currentTarget }) => {
          pinnedToBottom.current =
            currentTarget.scrollHeight - currentTarget.scrollTop - currentTarget.clientHeight <= PINNED_THRESHOLD_PX
        }}>
        {items.map(({ id, ...item }) => (
          <SupportThreadItem key={id} {...item} className="grid-cols-[96px_minmax(0,1fr)]" />
        ))}
        {summary && <SupportSummary {...summary} />}
        <SupportAgentComposer key={number} {...composer} />
      </div>
    </section>
  )
}

export const SupportAgentView = ({
  agentLabel,
  soundEnabled,
  onSoundToggled,
  onBackToPilote,
  queue,
  conversation,
  details,
}: SupportAgentViewProps) => {
  const intl = useIntl()
  const soundLabel = intl.formatMessage({ id: soundEnabled ? 'soutien.agent.sound.on' : 'soutien.agent.sound.off' })

  return (
    <div className="flex h-screen flex-col bg-gray-800">
      <header className="flex min-h-[49px] flex-wrap items-center justify-between gap-4 pr-10 pl-[26px]">
        <p className="text-xl text-orange-300 select-none">{intl.formatMessage({ id: 'soutien.panel.title' })}</p>
        <div className="flex flex-wrap items-center gap-4">
          <p className="text-base font-bold text-white select-none">{agentLabel}</p>
          <NotificationSound
            state={soundEnabled ? 'enabled' : 'muted'}
            aria-pressed={soundEnabled}
            aria-label={soundLabel}
            title={soundLabel}
            onClick={onSoundToggled}
          />
          <Button size="sm" className="h-[29px] bg-black px-6 py-1.5 hover:bg-gray-700" onClick={onBackToPilote}>
            {intl.formatMessage({ id: 'message.page.back' })}
          </Button>
        </div>
      </header>
      <main className="flex min-h-0 flex-1 gap-1 px-4 pb-4">
        <SupportAgentQueue {...queue} />
        {conversation ? (
          <SupportAgentThread {...conversation} />
        ) : (
          <section className="flex h-full min-w-0 flex-[999_1_480px] flex-col rounded-[20px] bg-white">
            <EmptyState
              icon={<MessagesSquareIcon aria-hidden className="size-8 stroke-[1.5] text-gray-500" />}
              message={intl.formatMessage({ id: 'soutien.agent.selectConversation' })}
            />
          </section>
        )}
        {details && <SupportAgentDetails {...details} />}
      </main>
    </div>
  )
}
